/* eslint-disable @next/next/no-before-interactive-script-outside-document */

"use client";

import { useEffect, useState } from "react";
import { hotjar } from "react-hotjar";
import LinkedInTag from "react-linkedin-insight";
import { useIntercom } from "react-use-intercom";
import { Helmet } from "react-helmet";
import { usePathname } from "next/navigation";

// helpers
import useUserProfile from "@/hooks/useUserProfile";

declare global {
  interface Window {
    OptanonWrapper: () => void;
    gtag: (command: string, obj: unknown) => void;
    OptanonActiveGroups: string[];
    dataLayer: Record<string, unknown>[];
  }
}

const mapSourceValue: { [key: string]: string } = {
  "WERI": "West",
  "LOCL": "Core"
};


const ConnectThirdPartyJs = () => {
  const [cookieConfig, setCookieConfig] = useState({
    isStrictlyNecessary: true,
    isPerformance: true,
    isFunctional: true,
    isTargeting: true
  });

  const { data: userProfile } = useUserProfile();
  const { update, shutdown, boot } = useIntercom();

  const source = mapSourceValue[process.env.NEXT_PUBLIC_APP_WL_CODE || "LOCL"];

  const pathname = usePathname();

  // methods
  const initHotjar = () => {
    if (process.env.NEXT_PUBLIC_HOTJAR_ID && cookieConfig.isFunctional) {
      hotjar.initialize({
        id: process.env.NEXT_PUBLIC_HOTJAR_ID as unknown as number,
        sv: 6,
      });
    }
  };

  const initGaUser = () => {
    if (userProfile && cookieConfig.isPerformance && window?.gtag) {
      window.gtag("set", { user_id: userProfile.id });
      window.gtag("set", { dimension1: userProfile.type });
    }
  };

  const initGA = () => {
    if (cookieConfig.isPerformance && window?.gtag) {
      window.gtag("pageview", window.location.pathname + window.location.search);
      window.gtag("send", { hitType: "pageview", page: "/" });
    }
  }
  

  const initLinkedIn = () => {
    LinkedInTag.init(
      process.env.NEXT_PUBLIC_LINKEDIN_PARTNER_ID as string,
      "dc",
      cookieConfig.isTargeting && (!!process.env.NEXT_PUBLIC_LINKEDIN_PARTNER_ID)
    );
  };

  const initGoogleTagManager = (gaId: string) => {
    if (!cookieConfig.isPerformance || !gaId) {
      return;
    }

    // Ensure the dataLayer array exists
    window.dataLayer = window.dataLayer || [];

    // Push the initial event to the dataLayer
    window.dataLayer.push({
      "gtm.start": new Date().getTime(),
      event: "gtm.js"
    });

    window.gtag = window.gtag || function() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer.push(arguments as unknown as Record<string, unknown>)
    }
  }

  // effects
  useEffect(() => {
    initHotjar();
    process.env.NEXT_PUBLIC_LINKEDIN_PARTNER_ID && initLinkedIn();
    initGoogleTagManager(process.env.NEXT_PUBLIC_G_TAG_ID as string);
    initGA()
  }, [JSON.stringify(cookieConfig)]);

  useEffect(() => {
    initGaUser();
  }, [JSON.stringify(userProfile), JSON.stringify(cookieConfig)]);

  useEffect(() => {
    if (typeof window !== "undefined" && !window.OptanonWrapper) {
      window.OptanonWrapper = () => {
        window.OneTrust?.OnConsentChanged(() => {
          setCookieConfig({
            isStrictlyNecessary: window.OptanonActiveGroups.includes("C0001"),
            isPerformance: window.OptanonActiveGroups.includes("C0002"),
            isFunctional: window.OptanonActiveGroups.includes("C0003"),
            isTargeting: window.OptanonActiveGroups.includes("C0004")
          });
        });
      }
    }
  }, []);

  useEffect(() => {
    try {
      if (userProfile?.id) {
        fetch(`/api/hmac-code/${userProfile.id}`).then(resp => resp.json().then(data => {
          update({
            userId: `${userProfile.id || Date.now()}`,
            email: userProfile.email,
            name: [userProfile.firstName, userProfile.lastName].filter(Boolean).join(" "),
            "source": source,
            userHash: data.hash,
            verticalPadding: 80,
          } as unknown as Record<string, string>);
        }))
      } else {
        shutdown();
        boot({
          verticalPadding: 80,
        });
      }
    } catch (e) {
      console.error("Intercom error:", e);
    }
  }, [userProfile?.id]);

  useEffect(() => {
    const regex = /^\/(uk|aus)\/lp(?:\/.*)?$/;

    if (regex.test(pathname)) {
      update({
        hideDefaultLauncher: true
      })
    } else {
      update({
        hideDefaultLauncher: false
      })
    }
  }, [pathname])

  return (<>
    <Helmet>
      <link rel="preconnect" href="https://dev.visualwebsiteoptimizer.com" />
      <script type="text/javascript" id="vwoCode">
        {`
        window._vwo_code || (function() {
          var account_id=883735,
          version=2.1,
          settings_tolerance=2000,
          hide_element='body',
          hide_element_style = 'opacity:0 !important;filter:alpha(opacity=0) !important;background:none !important',
          /* DO NOT EDIT BELOW THIS LINE */
          f=false,w=window,d=document,v=d.querySelector('#vwoCode'),cK='_vwo_'+account_id+'_settings',cc={};try{var c=JSON.parse(localStorage.getItem('_vwo_'+account_id+'_config'));cc=c&&typeof c==='object'?c:{}}catch(e){}var stT=cc.stT==='session'?w.sessionStorage:w.localStorage;code={use_existing_jquery:function(){return typeof use_existing_jquery!=='undefined'?use_existing_jquery:undefined},library_tolerance:function(){return typeof library_tolerance!=='undefined'?library_tolerance:undefined},settings_tolerance:function(){return cc.sT||settings_tolerance},hide_element_style:function(){return'{'+(cc.hES||hide_element_style)+'}'},hide_element:function(){if(performance.getEntriesByName('first-contentful-paint')[0]){return''}return typeof cc.hE==='string'?cc.hE:hide_element},getVersion:function(){return version},finish:function(e){if(!f){f=true;var t=d.getElementById('_vis_opt_path_hides');if(t)t.parentNode.removeChild(t);if(e)(new Image).src='https://dev.visualwebsiteoptimizer.com/ee.gif?a='+account_id+e}},finished:function(){return f},addScript:function(e){var t=d.createElement('script');t.type='text/javascript';if(e.src){t.src=e.src}else{t.text=e.text}d.getElementsByTagName('head')[0].appendChild(t)},load:function(e,t){var i=this.getSettings(),n=d.createElement('script'),r=this;t=t||{};if(i){n.textContent=i;d.getElementsByTagName('head')[0].appendChild(n);if(!w.VWO||VWO.caE){stT.removeItem(cK);r.load(e)}}else{var o=new XMLHttpRequest;o.open('GET',e,true);o.withCredentials=!t.dSC;o.responseType=t.responseType||'text';o.onload=function(){if(t.onloadCb){return t.onloadCb(o,e)}if(o.status===200){_vwo_code.addScript({text:o.responseText})}else{_vwo_code.finish('&e=loading_failure:'+e)}};o.onerror=function(){if(t.onerrorCb){return t.onerrorCb(e)}_vwo_code.finish('&e=loading_failure:'+e)};o.send()}},getSettings:function(){try{var e=stT.getItem(cK);if(!e){return}e=JSON.parse(e);if(Date.now()>e.e){stT.removeItem(cK);return}return e.s}catch(e){return}},init:function(){if(d.URL.indexOf('__vwo_disable__')>-1)return;var e=this.settings_tolerance();w._vwo_settings_timer=setTimeout(function(){_vwo_code.finish();stT.removeItem(cK)},e);var t;if(this.hide_element()!=='body'){t=d.createElement('style');var i=this.hide_element(),n=i?i+this.hide_element_style():'',r=d.getElementsByTagName('head')[0];t.setAttribute('id','_vis_opt_path_hides');v&&t.setAttribute('nonce',v.nonce);t.setAttribute('type','text/css');if(t.styleSheet)t.styleSheet.cssText=n;else t.appendChild(d.createTextNode(n));r.appendChild(t)}else{t=d.getElementsByTagName('head')[0];var n=d.createElement('div');n.style.cssText='z-index: 2147483647 !important;position: fixed !important;left: 0 !important;top: 0 !important;width: 100% !important;height: 100% !important;background: white !important;';n.setAttribute('id','_vis_opt_path_hides');n.classList.add('_vis_hide_layer');t.parentNode.insertBefore(n,t.nextSibling)}var o='https://dev.visualwebsiteoptimizer.com/j.php?a='+account_id+'&u='+encodeURIComponent(d.URL)+'&vn='+version;if(w.location.search.indexOf('_vwo_xhr')!==-1){this.addScript({src:o})}else{this.load(o+'&x=true')}}};w._vwo_code=code;code.init();})();
        `}
      </script>
      {process.env.SENTRY_ENVIRONMENT !== "production" && process.env.NEXT_PUBLIC_ONE_TRUST_ID && (
        <script
          defer
          src={`https://cdn-ukwest.onetrust.com/consent/${process.env.NEXT_PUBLIC_ONE_TRUST_ID}/OtAutoBlock.js`}
        />
      )}
      {
        process.env.NEXT_PUBLIC_ONE_TRUST_ID && (
          <script
            defer
            src="https://cdn-ukwest.onetrust.com/scripttemplates/otSDKStub.js"
            data-domain-script={process.env.NEXT_PUBLIC_ONE_TRUST_ID}
            data-testid="locl-script-onetrust"
            id="ot-sdk"
          />
        )
      }
      {
        process.env.NEXT_PUBLIC_APPZI_TOKEN && (
          <script
            async
            src={`https://w.appzi.io/w.js?token=${process.env.NEXT_PUBLIC_APPZI_TOKEN}`}
            type="text/javascript"
            data-testid="locl-script-appzi"
          />
        )
      }
      {
        (!!cookieConfig.isPerformance && !!process.env.NEXT_PUBLIC_G_TAG_ID) && ([
          <script data-testid="locl-gtag-script-preload" async key="gtag" src={`https://www.googletagmanager.com/gtag/js?id=${process.env.NEXT_PUBLIC_G_TAG_ID}`} />,
          <script data-testid="locl-gtag-script-inti" async key="gtagScript" id="google-analytics">
            {`
              (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
              new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
              j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
              'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
              })(window,document,'script','dataLayer','${process.env.NEXT_PUBLIC_G_TAG_ID}');
            `}
          </script>
        ])
      }
    </Helmet>
  </>)
};

export default ConnectThirdPartyJs;
